import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { ReceptorInput } from "../types";

interface ReceptorFormProps {
  value: ReceptorInput;
  onChange: (value: ReceptorInput) => void;
  errors?: Partial<Record<keyof ReceptorInput, string>>;
}

/**
 * Format RUT as user types (e.g., 12.345.678-9)
 */
function formatRut(value: string): string {
  // Remove all non-alphanumeric except K/k
  const clean = value.replace(/[^0-9kK]/g, "").toUpperCase();
  if (clean.length === 0) return "";

  // Separate body and verifier
  const verifier = clean.slice(-1);
  const body = clean.slice(0, -1);

  // Format body with dots
  const formattedBody = body.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  if (body.length === 0) return verifier;
  return `${formattedBody}-${verifier}`;
}

export function ReceptorForm({ value, onChange, errors }: ReceptorFormProps) {
  const handleChange = (field: keyof ReceptorInput, fieldValue: string) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleRutChange = (rawValue: string) => {
    const formatted = formatRut(rawValue);
    onChange({ ...value, rut: formatted });
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium">Datos del Receptor</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* RUT */}
        <div className="space-y-2">
          <Label htmlFor="rut">RUT</Label>
          <Input
            id="rut"
            value={value.rut}
            onChange={(e) => handleRutChange(e.target.value)}
            placeholder="12.345.678-9"
          />
          {errors?.rut && <p className="text-sm text-red-500">{errors.rut}</p>}
        </div>

        {/* Razón Social */}
        <div className="space-y-2">
          <Label htmlFor="razonSocial">Razón Social</Label>
          <Input
            id="razonSocial"
            value={value.razonSocial}
            onChange={(e) => handleChange("razonSocial", e.target.value)}
            placeholder="EMPRESA CLIENTE S.A."
          />
          {errors?.razonSocial && (
            <p className="text-sm text-red-500">{errors.razonSocial}</p>
          )}
        </div>

        {/* Giro */}
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="giro">Giro</Label>
          <Input
            id="giro"
            value={value.giro}
            onChange={(e) => handleChange("giro", e.target.value)}
            placeholder="COMERCIO AL POR MENOR"
          />
          {errors?.giro && (
            <p className="text-sm text-red-500">{errors.giro}</p>
          )}
        </div>

        {/* Dirección */}
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="direccion">Dirección</Label>
          <Input
            id="direccion"
            value={value.direccion}
            onChange={(e) => handleChange("direccion", e.target.value)}
            placeholder="Av. Libertador 1234"
          />
          {errors?.direccion && (
            <p className="text-sm text-red-500">{errors.direccion}</p>
          )}
        </div>

        {/* Comuna */}
        <div className="space-y-2">
          <Label htmlFor="comuna">Comuna</Label>
          <Input
            id="comuna"
            value={value.comuna}
            onChange={(e) => handleChange("comuna", e.target.value)}
            placeholder="Santiago"
          />
          {errors?.comuna && (
            <p className="text-sm text-red-500">{errors.comuna}</p>
          )}
        </div>

        {/* Ciudad */}
        <div className="space-y-2">
          <Label htmlFor="ciudad">Ciudad</Label>
          <Input
            id="ciudad"
            value={value.ciudad ?? ""}
            onChange={(e) => handleChange("ciudad", e.target.value)}
            placeholder="Santiago"
          />
        </div>
      </div>
    </div>
  );
}
